import { FunctionComponent, useEffect, useRef, useState } from 'react'
import ReactQuill from 'react-quill-new'
import { Button, Form } from 'react-bootstrap'
import { quillModules, getSemanticHtml } from 'Frontend/scripts/quillModules'
import { createWorkroomPostComment } from 'Frontend/scripts/services/workrooms.service'
import { useForm, Controller } from 'react-hook-form'
import { IAntiforgeryToken } from 'Frontend/scripts/models/general.model'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { getCurrentUser } from 'Frontend/scripts/services/users.service'
import { IUser } from 'Frontend/scripts/models/user.model'
import { useTranslation } from 'react-i18next'

interface CreateWorkroomPostCommentProps {
  workroomId: number
  postId: number
  callback: () => void | null
}

const CreateWorkroomPostComment: FunctionComponent<CreateWorkroomPostCommentProps> = ({
  workroomId,
  postId,
  callback,
}) => {
  const quillRef = useRef<ReactQuill | null>(null)
  const todaysDate = new Date().toJSON().slice(0, 10)
  const [user, setUser] = useState<IUser>()
  const { t } = useTranslation()

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      text: '',
    },
  })

  const onSubmit = () => {
    const mainBodyWithCorrectHTML = getSemanticHtml(quillRef)
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      createWorkroomPostComment(token.csrfToken, workroomId, postId, mainBodyWithCorrectHTML).then(() => {
        callback()
        reset()
        setValue('text', '', { shouldValidate: false })
        if (quillRef.current) {
          quillRef.current.getEditor().setContents([])
        }
      })
    })
  }

  useEffect(() => {
    getCurrentUser().then((user) => {
      setUser(user)
    })
  }, [])

  return (
    <div className="livstecknet-thread__answer-area mt-3 p-3">
      <div className="d-flex gap-2">
        {user?.profileImageUrl != null ? (
          <div>
            <img
              className="workrooms__profile-image"
              src={user.profileImageUrl}
              alt={`${t('image-of')} ${user?.firstname} ${user?.lastname}`}
            />
          </div>
        ) : (
          <div className="livstecknet-thread__profile-initial-letters">
            {user?.firstname.charAt(0)}
            {user?.lastname.charAt(0)}
          </div>
        )}
        <div className="d-flex flex-column justify-content-center">
          <a href={user?.viewProfileUrl} className="livstecknet-thread__author">
            {user?.firstname}&nbsp;
            {user?.lastname}
          </a>
          <p className="livstecknet-thread__time mb-0">{todaysDate}</p>
        </div>
      </div>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column gap-3">
        <Form.Group>
          <Form.Label htmlFor="inputText">{t('text')}</Form.Label>
          <Controller
            name="text"
            control={control}
            rules={{
              validate: (value) => value?.replace(/<(.|\n)*?>/g, '').trim().length > 0 || t('error-text'),
            }}
            render={({ field }) => (
              <ReactQuill
                {...field}
                ref={quillRef}
                id="inputDescription"
                className="notice__quill-editor"
                theme="snow"
                modules={quillModules}
                value={field.value || ''}
                onChange={(value) => setValue('text', value, { shouldValidate: true })}
              />
            )}
          />
          {errors.text && <Form.Text className="text-danger">{errors.text.message}</Form.Text>}
        </Form.Group>

        <div className="d-flex gap-3">
          <Button variant="secondary" type="submit">
            {t('answer-on-post')}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default CreateWorkroomPostComment
